import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useCallback } from 'react'
import useSWR from 'swr'

/** The daily fit series saved alongside a model, so its expected-vs-actual can
 * be drawn without re-pulling or re-fitting. */
/** Whether the baseline band still describes the plant being graded. */
export interface ModelHealth {
  window_days: number
  recent_scale: number | null
  /** Recent gap scatter over the baseline band; 2 and up is out of range. */
  ratio: number | null
  status: 'ok' | 'out_of_range' | 'unknown'
}

export interface ModelSeries {
  /** Date-only when the model grades daily, full stamps when it grades finer. */
  dates: string[]
  actual: (number | null)[]
  expected: (number | null)[]
  err_scale: number
  model_health?: ModelHealth
  smoothing_days?: number
  /** [start, end) spans of rows the fit did not use: gated out or masked. */
  dropped?: string[][]
  /** True when a flapping gate forced nearby spans to be merged: coverage is
   *  complete either way, the edges are just coarser than the samples. */
  dropped_coalesced?: boolean
}

export interface AgentModel {
  entry_id: string
  name?: string
  updated_at?: string
  kpi?: string
  expr?: string
  roles?: Record<string, string>
  drivers?: string[]
  gate_expr?: string
  baseline_window?: [string, string]
  baseline_rows?: number
  baseline_median?: number
  /** One sigma: the standard deviation of the baseline's daily fit error. */
  err_scale?: number
  err_scale_robust?: number
  row_scale?: number
  band_source?: string
  model_health?: ModelHealth
  /** The alert rule; `level_sigma` is the k the chart band is drawn at. */
  monitor?: { level_sigma?: number }
  last30_actual?: number
  last30_expected?: number
  last30_gap?: number
  last30_sigma?: number
  data_through?: string
  created_at?: string
}

/** The SWR key for a site's model list, shared by every mount of the hook and
 * by the chat stream's saved-frame revalidation. */
export const agentModelsUrl = (fimServiceUrl: string, siteId: string): string =>
  `${fimServiceUrl}/api/v1/sites/${encodeURIComponent(siteId)}/sentinel-chat/models`

/** The site's saved agent models — the durable artifacts the agent builds
 * and edits. The list payload carries each model's full spec. SWR-cached on
 * the models URL, so the tab and the dock render one list and either one's
 * `refresh` updates both. */
export const useAgentModels = (siteId: string | null) => {
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { authorizedAxios } = useGeckoAuth()
  const base = siteId ? agentModelsUrl(FIM_SERVICE_URL, siteId) : null

  const { data, isLoading, mutate } = useSWR(base, async (url: string) => {
    const { data } = await authorizedAxios.get<{ models: AgentModel[] }>(url)
    return [...(data.models ?? [])].sort((a, b) =>
      (b.updated_at ?? '').localeCompare(a.updated_at ?? '')
    )
  })

  const fetchSeries = useCallback(
    async (entryId: string): Promise<ModelSeries | null> => {
      if (!siteId) return null
      try {
        const { data } = await authorizedAxios.get<{ series: ModelSeries }>(
          `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteId)}` +
            `/sentinel-chat/models/${encodeURIComponent(entryId)}/series`
        )
        return data.series ?? null
      } catch {
        // pre-series models simply have nothing saved to draw
        return null
      }
    },
    [FIM_SERVICE_URL, authorizedAxios, siteId]
  )

  const deleteModel = useCallback(
    async (entryId: string): Promise<boolean> => {
      if (!siteId) return false
      try {
        await authorizedAxios.delete(
          `${agentModelsUrl(FIM_SERVICE_URL, siteId)}/${encodeURIComponent(entryId)}`
        )
        await mutate(
          (prev) => (prev ?? []).filter((m) => m.entry_id !== entryId),
          { revalidate: false }
        )
        return true
      } catch {
        return false
      }
    },
    [FIM_SERVICE_URL, authorizedAxios, mutate, siteId]
  )

  return {
    models: data ?? [],
    loading: isLoading,
    refresh: mutate,
    fetchSeries,
    deleteModel,
  }
}
